import type { UniversalCamera } from "@babylonjs/core";
import { DT, FIXTURES, PISTOL, PLAYER, SPYGLASS, type Body, type Vec3 } from "@tiny-seas/shared";
import { STROKE } from "../audio/gameplay.ts";
import type { CharacterPose } from "../character/character-pose.ts";
import { gaitFor } from "../character/gait.ts";
import { createSwordBody, sampleSwordBodyToRef } from "./sword-body.ts";

/**
 * The local sailor's presentation, shared by the game and the interaction lab so
 * the lab shows exactly what gameplay draws: body speed and posture from the fixed
 * 30 Hz physics step, render interpolation, and the first-person camera (eye,
 * bob, landing dip, sprint FOV, lens zoom, sailing sway and shake).
 */

/** Vertical FOV in radians: 90° horizontal at 16:9, held fixed across aspect ratios. */
export const BASE_VERTICAL_FOV = 2 * Math.atan(Math.tan(Math.PI / 4) * 9 / 16);
const SPRINT_VERTICAL_FOV = 2 * Math.atan(Math.tan(94 * Math.PI / 360) * 9 / 16);
const LANDING_DIP_SECONDS = 0.18;

const smooth = (t: number): number => t * t * (3 - 2 * t);

/** Support-frame ground speed of the latest physics step; 0 across a frame change. */
export function bodySpeed(prev: Readonly<Body>, b: Readonly<Body>): number {
  return prev.frame === b.frame ? Math.min(8, Math.hypot(b.x - prev.x, b.z - prev.z) / DT) : 0;
}

/** Render position between the last two physics steps (`alpha` = accumulator / DT). */
export function interpolateFeet(prev: Readonly<Body>, b: Readonly<Body>, alpha: number, out: Vec3): Vec3 {
  if (prev.frame === b.frame) {
    out.x = prev.x + (b.x - prev.x) * alpha;
    out.y = prev.y + (b.y - prev.y) * alpha;
    out.z = prev.z + (b.z - prev.z) * alpha;
  } else { out.x = b.x; out.y = b.y; out.z = b.z; }
  return out;
}

/** Locomotion fields of the local sailor's pose, straight from the physics body. */
export function applyBodyToPose(pose: CharacterPose, b: Readonly<Body>, speed: number, deckHeading: number): void {
  pose.speed = speed;
  pose.crouched = b.crouched;
  pose.sprinting = b.sprinting;
  pose.slideProgress = b.slideRemaining > 0 ? 1 - b.slideRemaining / PLAYER.slideDuration : -1;
  pose.slideYaw = b.slideYaw + deckHeading;
  pose.grounded = b.grounded;
  pose.verticalSpeed = b.vy;
}

/** Optical zoom: a mounted telescope, else the raised spyglass, else the revolver's aim. */
export function lensMagnification(scopeBlend: number, pistolAimBlend: number, telescopeBlend: number): number {
  return telescopeBlend > 0 ? 1 + (FIXTURES.telescopeMagnification - 1) * smooth(telescopeBlend) :
    scopeBlend > 0 ? 1 + (SPYGLASS.magnification - 1) * smooth(scopeBlend) :
    1 + (PISTOL.aimMagnification - 1) * smooth(pistolAimBlend);
}

export interface FirstPersonFrame {
  dt: number;
  /** Seconds, for the sailing sway. */
  time: number;
  feet: Readonly<Vec3>;
  /** Posture eye the camera eases toward (slide, crouch, swim or standing). */
  targetEye: number;
  /** World-space eye offset, e.g. stepping back from a ladder to keep both grips in view. */
  offsetX: number;
  offsetZ: number;
  yaw: number;
  pitch: number;
  speed: number;
  /** Grounded ordinary walking: drives the head bob. */
  walking: boolean;
  grounded: boolean;
  onStation: boolean;
  atWheel: boolean;
  ladder: boolean;
  climbProgress: number;
  crouched: boolean;
  /** In the water (swim mode): surface strokes lift the head, treading bobs it gently. */
  swimming: boolean;
  /** Rendered sea height a surface swimmer rides (`CharacterPose.waterHeave`). */
  waterHeave: number;
  /** Standing or crouched capsule height. */
  bodyHeight: number;
  sprinting: boolean;
  carrying: boolean;
  /** 0…1 through a slide, else -1. */
  slideProgress: number;
  scopeBlend: number;
  telescopeBlend: number;
  magnification: number;
  /** 0…1 deck motion while the ship is under way, and its rudder. */
  sailBlend: number;
  rudder: number;
  reducedMotion: boolean;
  /** Decorative motion is allowed (alive, not inside a cannon barrel). */
  decorate: boolean;
  /** Seconds into the current melee attack, else -1. */
  attackPhase: number;
  attackStep: number;
  /** Seconds since a cutlass struck this sailor, else -1; and that cut's combo step. */
  hitFlinch: number;
  hitStep: number;
}

export function createFirstPersonFrame(): FirstPersonFrame {
  return {
    dt: 0, time: 0, feet: { x: 0, y: 0, z: 0 }, targetEye: PLAYER.eyeHeight, offsetX: 0, offsetZ: 0,
    yaw: 0, pitch: 0, speed: 0, walking: false, grounded: true, onStation: false, atWheel: false, ladder: false, climbProgress: 0,
    crouched: false, swimming: false, waterHeave: 0, bodyHeight: PLAYER.height, sprinting: false, carrying: false, slideProgress: -1,
    scopeBlend: 0, telescopeBlend: 0, magnification: 1, sailBlend: 0, rudder: 0, reducedMotion: false, decorate: true,
    attackPhase: -1, attackStep: 0, hitFlinch: -1, hitStep: 0,
  };
}

export class FirstPersonView {
  /** Eased eye height above the feet. */
  eyeHeight: number = PLAYER.eyeHeight;
  private walkPhase = 0;
  private swimPhase = 0;
  private landing = 0;
  private wasGrounded = true;
  private sprintBlend = 0;
  private shakeAmount = 0;
  private readonly swordBody = createSwordBody();

  /** Nearby cannon fire or impact. */
  shake(amount = 1): void {
    this.shakeAmount = Math.max(this.shakeAmount, amount);
  }

  reset(): void {
    this.walkPhase = this.swimPhase = this.landing = this.sprintBlend = this.shakeAmount = 0;
    this.wasGrounded = true;
    this.eyeHeight = PLAYER.eyeHeight;
  }

  update(camera: UniversalCamera, f: Readonly<FirstPersonFrame>): void {
    const dt = f.dt, feet = f.feet, yaw = f.yaw;
    this.eyeHeight += (f.targetEye - this.eyeHeight) * Math.min(1, dt / 0.15);
    if (f.crouched) this.eyeHeight = Math.min(this.eyeHeight, f.bodyHeight - 0.05);
    camera.position.set(feet.x + f.offsetX, feet.y + this.eyeHeight + f.waterHeave, feet.z + f.offsetZ);
    camera.rotation.set(f.pitch, yaw, 0);

    // π per footfall: the bob dips once a step and the sway crosses once a stride, in time with the legs.
    if (f.walking) this.walkPhase += f.speed * dt * Math.PI / gaitFor(f.crouched, f.sprinting, f.carrying).step;
    if (f.grounded && !this.wasGrounded && !f.onStation) this.landing = LANDING_DIP_SECONDS;
    this.wasGrounded = f.grounded;
    this.landing = Math.max(0, this.landing - dt);
    this.shakeAmount = Math.max(0, this.shakeAmount - dt * 2.5);

    const scopeSmooth = smooth(f.scopeBlend), telescopeSmooth = smooth(f.telescopeBlend);
    this.sprintBlend += ((f.sprinting ? 1 : 0) - this.sprintBlend) * Math.min(1, dt / 0.20);
    // Decorative camera motion fades with any optical zoom: the spyglass or a mounted telescope.
    const motion = (1 - scopeSmooth) * (1 - telescopeSmooth);
    const baseFov = !f.reducedMotion ? BASE_VERTICAL_FOV + (SPRINT_VERTICAL_FOV - BASE_VERTICAL_FOV) * this.sprintBlend * motion : BASE_VERTICAL_FOV;
    camera.fov = 2 * Math.atan(Math.tan(baseFov / 2) / f.magnification);
    if (!f.reducedMotion && f.decorate && motion > 0) {
      const baseX = camera.position.x, baseY = camera.position.y, baseZ = camera.position.z;
      const t = f.time;
      const walk = f.walking ? Math.min(1, f.speed / 4) : 0;
      const sway = Math.sin(this.walkPhase) * walk;
      const lateral = sway * 0.025;
      camera.position.x += Math.cos(yaw) * lateral;
      camera.position.z -= Math.sin(yaw) * lateral;
      camera.position.y += Math.cos(this.walkPhase * 2) * walk * 0.055 - Math.sin(this.landing / LANDING_DIP_SECONDS * Math.PI) * 0.05;
      camera.rotation.z += sway * 0.015;
      camera.position.y += Math.sin(t * 1.7) * 0.08 * f.sailBlend * (f.atWheel ? 0.5 : 1);
      camera.rotation.z += (Math.sin(t * 1.15) * 0.035 + f.rudder * 0.025) * f.sailBlend;
      camera.rotation.x += Math.sin(t * 1.4) * 0.018 * f.sailBlend;
      if (f.ladder) camera.position.y += Math.sin(f.climbProgress * Math.PI * 8) * 0.02;
      if (f.swimming && feet.y >= PLAYER.surfaceFeetY - 0.05) {
        // Each breaststroke pull lifts the head; treading water rises and settles slowly.
        this.swimPhase = (this.swimPhase + f.speed * dt / STROKE) % 1;
        const stroke = Math.min(1, f.speed / PLAYER.swimSpeed);
        camera.position.y += Math.sin(this.swimPhase * Math.PI * 2) * 0.035 * stroke + Math.sin(t * 1.6) * 0.02 * (1 - stroke);
      }
      // Each cut kicks the view its own way; the finisher pitches down and lunges the eye forward.
      const kick = sampleSwordBodyToRef(f.attackStep, f.attackPhase, f.hitStep, f.hitFlinch, this.swordBody);
      camera.rotation.x += kick.viewPitch;
      camera.rotation.y += kick.viewYaw * motion;
      camera.rotation.z += kick.viewRoll;
      camera.position.x += Math.sin(yaw) * kick.viewPush;
      camera.position.z += Math.cos(yaw) * kick.viewPush;
      camera.position.y -= kick.viewDrop;
      camera.position.x += (Math.random() - 0.5) * this.shakeAmount * 0.12;
      camera.position.y += (Math.random() - 0.5) * this.shakeAmount * 0.12;
      if (f.slideProgress >= 0) camera.rotation.z += Math.sin(f.slideProgress * Math.PI) * 0.04;
      // Fade decorative offsets with the lens raise instead of snapping them off at its first frame.
      camera.position.x = baseX + (camera.position.x - baseX) * motion;
      camera.position.y = baseY + (camera.position.y - baseY) * motion;
      camera.position.z = baseZ + (camera.position.z - baseZ) * motion;
      camera.rotation.x = f.pitch + (camera.rotation.x - f.pitch) * motion;
      camera.rotation.z *= motion;
    }
    if (f.crouched) camera.position.y = Math.min(camera.position.y, feet.y + f.bodyHeight - 0.05);
  }
}
